import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface LearningMetrics {
  scoring_accuracy: number;
  outreach_effectiveness: number;
  objection_success_rate: number;
  pricing_win_rate: number;
  icp_match_rate: number;
}

interface OptimizationSuggestion {
  type: 'scoring' | 'outreach' | 'objection' | 'pricing' | 'icp';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  data?: Record<string, any>;
}

export function useAILearningLoop() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch learning metrics based on historical data
  const { data: learningMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['ai-learning-metrics'],
    queryFn: async (): Promise<LearningMetrics> => {
      // Fetch deals to calculate scoring accuracy
      const { data: deals } = await supabase
        .from('deals')
        .select('id, status, ai_win_probability')
        .not('status', 'eq', 'open');

      // Calculate scoring accuracy (how often high-probability deals were won)
      const predictedWins = deals?.filter(d => (d.ai_win_probability || 0) >= 70) || [];
      const actualWins = predictedWins.filter(d => d.status === 'won');
      const scoringAccuracy = predictedWins.length > 0 
        ? (actualWins.length / predictedWins.length) * 100 
        : 75;

      // Fetch outreach effectiveness
      const { data: sequences } = await supabase
        .from('sequence_enrollments')
        .select('status');
      
      const completedSequences = sequences?.filter(s => s.status === 'completed').length || 0;
      const totalSequences = sequences?.length || 1;
      const outreachEffectiveness = (completedSequences / totalSequences) * 100;

      // Fetch objection success rate
      const { data: objections } = await supabase
        .from('objection_library')
        .select('success_rate, usage_count');
      
      const totalUsage = objections?.reduce((sum, o) => sum + (o.usage_count || 0), 0) || 0;
      const weightedSuccess = objections?.reduce((sum, o) => 
        sum + ((o.success_rate || 0) * (o.usage_count || 0)), 0) || 0;
      const objectionSuccessRate = totalUsage > 0 ? weightedSuccess / totalUsage : 65;

      // Calculate pricing win rate
      const wonDeals = deals?.filter(d => d.status === 'won').length || 0;
      const totalClosedDeals = deals?.length || 1;
      const pricingWinRate = (wonDeals / totalClosedDeals) * 100;

      // Fetch ICP match rate from leads
      const { data: leads } = await supabase
        .from('leads')
        .select('lead_score, status');
      
      const highScoreLeads = leads?.filter(l => (l.lead_score || 0) >= 70) || [];
      const convertedHighScore = highScoreLeads.filter(l => l.status === 'converted').length;
      const icpMatchRate = highScoreLeads.length > 0 
        ? (convertedHighScore / highScoreLeads.length) * 100 
        : 60;

      return {
        scoring_accuracy: Math.round(scoringAccuracy),
        outreach_effectiveness: Math.round(outreachEffectiveness),
        objection_success_rate: Math.round(objectionSuccessRate),
        pricing_win_rate: Math.round(pricingWinRate),
        icp_match_rate: Math.round(icpMatchRate),
      };
    },
  });

  // Generate optimization suggestions
  const { data: suggestions, isLoading: suggestionsLoading } = useQuery({
    queryKey: ['ai-optimization-suggestions', learningMetrics],
    enabled: !!learningMetrics,
    queryFn: async (): Promise<OptimizationSuggestion[]> => {
      const result: OptimizationSuggestion[] = [];

      if (!learningMetrics) return result;

      // Scoring suggestions
      if (learningMetrics.scoring_accuracy < 70) {
        result.push({
          type: 'scoring',
          title: 'Improve Lead Scoring Model',
          description: 'Current scoring accuracy is below target. Consider retraining with recent conversion data.',
          impact: 'high',
        });
      }

      // Outreach suggestions
      if (learningMetrics.outreach_effectiveness < 50) {
        result.push({
          type: 'outreach',
          title: 'Optimize Outreach Sequences',
          description: 'Sequence completion rate is low. AI suggests adjusting timing and messaging.',
          impact: 'high',
        });
      }

      // Objection handling suggestions
      if (learningMetrics.objection_success_rate < 60) {
        result.push({
          type: 'objection',
          title: 'Update Objection Responses',
          description: 'Some objection responses are underperforming. Review and update based on recent calls.',
          impact: 'medium',
        });
      }

      // Pricing suggestions
      if (learningMetrics.pricing_win_rate < 40) {
        result.push({
          type: 'pricing',
          title: 'Adjust Pricing Strategy',
          description: 'Win rate indicates pricing may need optimization. Consider competitive analysis.',
          impact: 'high',
        });
      }

      // ICP suggestions
      if (learningMetrics.icp_match_rate < 50) {
        result.push({
          type: 'icp',
          title: 'Refine ICP Targeting',
          description: 'High-score leads are not converting well. ICP criteria may need adjustment.',
          impact: 'medium',
        });
      }

      // Add positive reinforcement if metrics are good
      if (result.length === 0) {
        result.push({
          type: 'scoring',
          title: 'AI Models Performing Well',
          description: 'All metrics are above target. Continue monitoring for optimization opportunities.',
          impact: 'low',
        });
      }

      return result;
    },
  });

  // Trigger AI model retraining
  const retrainModel = useMutation({
    mutationFn: async (modelType: 'scoring' | 'outreach' | 'objection' | 'pricing') => {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          type: 'retrain_model',
          context: { model_type: modelType },
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, modelType) => {
      toast({
        title: "Retraining Started",
        description: `AI ${modelType} model is being retrained with latest data.`,
      });
      queryClient.invalidateQueries({ queryKey: ['ai-learning-metrics'] });
    },
    onError: (error) => {
      toast({
        title: "Retraining Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Apply optimization suggestion
  const applyOptimization = useMutation({
    mutationFn: async (suggestion: OptimizationSuggestion) => {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          type: 'apply_optimization',
          context: { 
            optimization_type: suggestion.type,
            suggestion: suggestion.description,
          },
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Optimization Applied",
        description: "AI has applied the suggested optimization.",
      });
      queryClient.invalidateQueries({ queryKey: ['ai-optimization-suggestions'] });
    },
    onError: (error) => {
      toast({
        title: "Optimization Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    learningMetrics,
    metricsLoading,
    suggestions,
    suggestionsLoading,
    retrainModel,
    applyOptimization,
  };
}
